import { useState, useEffect } from 'react';
import './ConviteEvento.css';
import CalrizLogo from '../../assets/logos/logo-oficial-calriz.png';
import GoogleCloudLogo from '../../assets/logos/google-cloud.png';
import Seo from '../../seo/Seo';

const REGISTRATION_URL = 'https://backend.calriz.com/api/registro-evento';
const EVENT_DATE = new Date('2026-10-28T13:00:00-05:00');

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function calcTimeLeft(): TimeLeft {
  const diff = EVENT_DATE.getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

function maskTelefono(raw: string): string {
  const cleaned = raw.replace(/[^\d+\s]/g, '');
  return cleaned.startsWith('+') ? cleaned.slice(0, 20) : cleaned.replace(/\+/g, '').slice(0, 20);
}

function isValidTelefono(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

export default function RegistrationColombia() {
  const [step, setStep] = useState(1);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calcTimeLeft);
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [cargo, setCargo] = useState('');
  const [organizacao, setOrganizacao] = useState('');
  const [telefone, setTelefone] = useState('');
  const [acessibilidade, setAcessibilidade] = useState<'no' | 'si'>('no');
  const [acessibilidadeDetalhe, setAcessibilidadeDetalhe] = useState('');
  const [alimentar, setAlimentar] = useState<'no' | 'si'>('no');
  const [alimentarDetalhe, setAlimentarDetalhe] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    nome?: string;
    cargo?: string;
    organizacao?: string;
    telefone?: string;
    acessibilidade?: string;
    alimentar?: string;
  }>({});

  useEffect(() => {
    const interval = setInterval(() => setTimeLeft(calcTimeLeft()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (submitted) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [submitted]);

  async function handleSubmit() {
    if (alimentar === 'si' && !alimentarDetalhe.trim()) {
      setErrors(e => ({ ...e, alimentar: 'Por favor, especifique la restricción alimentaria.' }));
      return;
    }
    const payload = {
      email,
      nome,
      organizacao,
      cargo,
      telefone,
      cidade: 'colombia',
      acessibilidade: acessibilidade === 'si' ? acessibilidadeDetalhe.trim() : 'No',
      restricaoAlimentar: alimentar === 'si' ? alimentarDetalhe.trim() : 'No',
    };
    if (!REGISTRATION_URL) {
      console.log('Registration payload:', payload);
      setSubmitted(true);
      return;
    }
    try {
      const res = await fetch(REGISTRATION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Network response not ok');
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setErrors(e => ({ ...e, alimentar: 'Error al enviar la inscripción. Inténtelo de nuevo.' }));
    }
  }

  function handleNextEmail() {
    if (!email.trim()) {
      setErrors(e => ({ ...e, email: 'Ingrese el correo electrónico.' }));
      return;
    }
    if (!isValidEmail(email)) {
      setErrors(e => ({ ...e, email: 'Correo electrónico inválido. Verifique el formato.' }));
      return;
    }
    setErrors(e => ({ ...e, email: undefined }));
    setStep(2);
  }

  function handleNextNome() {
    if (!nome.trim()) {
      setErrors(e => ({ ...e, nome: 'Ingrese el nombre completo.' }));
      return;
    }
    setErrors(e => ({ ...e, nome: undefined }));
    setStep(3);
  }

  function handleNextCargo() {
    if (!cargo.trim()) {
      setErrors(e => ({ ...e, cargo: 'Ingrese el cargo.' }));
      return;
    }
    setErrors(e => ({ ...e, cargo: undefined }));
    setStep(4);
  }

  function handleNextOrganizacao() {
    if (!organizacao.trim()) {
      setErrors(e => ({ ...e, organizacao: 'Ingrese la entidad o institución.' }));
      return;
    }
    setErrors(e => ({ ...e, organizacao: undefined }));
    setStep(5);
  }

  function handleNextTelefone() {
    if (!isValidTelefono(telefone)) {
      setErrors(e => ({ ...e, telefone: 'Teléfono inválido. Incluya el indicativo y el número completo.' }));
      return;
    }
    setErrors(e => ({ ...e, telefone: undefined }));
    setStep(6);
  }

  function handleNextAcessibilidade() {
    if (acessibilidade === 'si' && !acessibilidadeDetalhe.trim()) {
      setErrors(e => ({ ...e, acessibilidade: 'Por favor, especifique la adaptación necesaria.' }));
      return;
    }
    setErrors(e => ({ ...e, acessibilidade: undefined }));
    setStep(7);
  }

  function stepClass(n: number) {
    if (n === step) return 'reg-step reg-step--active';
    if (n < step) return 'reg-step reg-step--done';
    return 'reg-step';
  }

  if (submitted) {
    return (
      <div className="rsvp-page reg-page">
        <nav className="rsvp-nav">
          <div className="rsvp-nav-inner">
            <div className="rsvp-nav-left">
              <img src={CalrizLogo} alt="Calriz" className="rsvp-nav-calriz-logo" />
              <span className="rsvp-nav-divider" />
              <img src={GoogleCloudLogo} alt="Google Cloud" className="rsvp-nav-gc-logo" />
            </div>
          </div>
        </nav>
        <div className="reg-container">
          <div className="reg-success">
            <svg className="reg-success-icon" viewBox="0 0 24 24" fill="none" stroke="#1a73e8" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="9 12 11 14 15 10" />
            </svg>
            <h2>¡Solicitud enviada!</h2>
            <p>
              Gracias. Los cupos son limitados y la inscripción está sujeta a confirmación.
              Recibirá un correo electrónico con la respuesta.
            </p>
            <a href="/google-para-ciudades-colombia" className="rsvp-btn-pill">Volver al evento</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rsvp-page reg-page">
      <Seo title="Inscripción – Google para Ciudades Colombia" description="Inscríbase en Google para Ciudades – Colombia, encuentro de Calriz y Google en las oficinas de Google Bogotá, el 28 de octubre de 2026." path="/google-para-ciudades-colombia/inscripcion" noindex />
      {/* Navbar */}
      <nav className="rsvp-nav">
        <div className="rsvp-nav-inner">
          <div className="rsvp-nav-left">
            <img src={CalrizLogo} alt="Calriz" className="rsvp-nav-calriz-logo" />
            <img src={GoogleCloudLogo} alt="Google Cloud" className="rsvp-nav-gc-logo" />
            <ul className="rsvp-nav-links">
              <li>
                <a href="/google-para-ciudades-colombia" className="rsvp-nav-link rsvp-nav-link--active">Inicio</a>
              </li>
            </ul>
          </div>

          <div className="rsvp-nav-right">
            <span className="rsvp-countdown">
              Comienza en{' '}
              <strong>
                {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
              </strong>
            </span>
          </div>
        </div>
      </nav>

      <div className="reg-container">

        {/* Header */}
        <div className="reg-header">
          <h1 className="reg-event-title">Google para Ciudades – Colombia</h1>
          <div className="reg-event-date">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="reg-date-icon">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span>28 de octubre de 2026 · 1:00 p. m. a 5:00 p. m. · Oficinas de Google Bogotá</span>
          </div>
        </div>

        {/* Step 1: Correo */}
        <div className={stepClass(1)}>
          <div className="reg-step-number">1</div>
          <div className="reg-step-body">
            <div className="reg-step-title">Correo electrónico corporativo</div>
            {step === 1 && (
              <div className="reg-step-content">
                <input
                  type="email"
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors(ev => ({ ...ev, email: undefined }));
                  }}
                  onKeyDown={e => e.key === 'Enter' && handleNextEmail()}
                  placeholder="Ingrese el correo electrónico"
                  className={`rsvp-input reg-email-input${errors.email ? ' reg-input--error' : ''}`}
                  maxLength={80}
                  autoFocus
                />
                {errors.email && <p className="reg-field-error">{errors.email}</p>}
                <div className="reg-step-footer">
                  <button
                    type="button"
                    style={{ cursor: 'pointer' }}
                    className="rsvp-btn-pill"
                    onClick={handleNextEmail}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
            {step > 1 && <div className="reg-step-summary">{email}</div>}
          </div>
        </div>

        {/* Step 2: Nombre */}
        <div className={stepClass(2)}>
          <div className="reg-step-number">2</div>
          <div className="reg-step-body">
            <div className="reg-step-title">Nombre completo</div>
            {step === 2 && (
              <div className="reg-step-content">
                <input
                  type="text"
                  value={nome}
                  onChange={e => {
                    setNome(e.target.value);
                    if (errors.nome) setErrors(ev => ({ ...ev, nome: undefined }));
                  }}
                  onKeyDown={e => e.key === 'Enter' && handleNextNome()}
                  placeholder="Nombre completo"
                  className={`rsvp-input reg-email-input${errors.nome ? ' reg-input--error' : ''}`}
                  maxLength={80}
                  autoFocus
                />
                {errors.nome && <p className="reg-field-error">{errors.nome}</p>}
                <div className="reg-step-footer">
                  <button type="button" className="reg-btn-back" onClick={() => setStep(1)}>Volver</button>
                  <button
                    type="button"
                    style={{ cursor: 'pointer' }}
                    className="rsvp-btn-pill"
                    onClick={handleNextNome}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
            {step > 2 && <div className="reg-step-summary">{nome}</div>}
          </div>
        </div>

        {/* Step 3: Cargo */}
        <div className={stepClass(3)}>
          <div className="reg-step-number">3</div>
          <div className="reg-step-body">
            <div className="reg-step-title">Cargo</div>
            {step === 3 && (
              <div className="reg-step-content">
                <input
                  type="text"
                  value={cargo}
                  onChange={e => {
                    setCargo(e.target.value);
                    if (errors.cargo) setErrors(ev => ({ ...ev, cargo: undefined }));
                  }}
                  onKeyDown={e => e.key === 'Enter' && handleNextCargo()}
                  placeholder="Cargo o función"
                  className={`rsvp-input reg-email-input${errors.cargo ? ' reg-input--error' : ''}`}
                  maxLength={80}
                  autoFocus
                />
                {errors.cargo && <p className="reg-field-error">{errors.cargo}</p>}
                <div className="reg-step-footer">
                  <button type="button" className="reg-btn-back" onClick={() => setStep(2)}>Volver</button>
                  <button
                    type="button"
                    style={{ cursor: 'pointer' }}
                    className="rsvp-btn-pill"
                    onClick={handleNextCargo}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
            {step > 3 && <div className="reg-step-summary">{cargo}</div>}
          </div>
        </div>

        {/* Step 4: Entidad */}
        <div className={stepClass(4)}>
          <div className="reg-step-number">4</div>
          <div className="reg-step-body">
            <div className="reg-step-title">Entidad o institución</div>
            {step === 4 && (
              <div className="reg-step-content">
                <input
                  type="text"
                  value={organizacao}
                  onChange={e => {
                    setOrganizacao(e.target.value);
                    if (errors.organizacao) setErrors(ev => ({ ...ev, organizacao: undefined }));
                  }}
                  onKeyDown={e => e.key === 'Enter' && handleNextOrganizacao()}
                  placeholder="Entidad o institución"
                  className={`rsvp-input reg-email-input${errors.organizacao ? ' reg-input--error' : ''}`}
                  maxLength={80}
                  autoFocus
                />
                {errors.organizacao && <p className="reg-field-error">{errors.organizacao}</p>}
                <div className="reg-step-footer">
                  <button type="button" className="reg-btn-back" onClick={() => setStep(3)}>Volver</button>
                  <button
                    type="button"
                    style={{ cursor: 'pointer' }}
                    className="rsvp-btn-pill"
                    onClick={handleNextOrganizacao}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
            {step > 4 && <div className="reg-step-summary">{organizacao}</div>}
          </div>
        </div>

        {/* Step 5: Teléfono */}
        <div className={stepClass(5)}>
          <div className="reg-step-number">5</div>
          <div className="reg-step-body">
            <div className="reg-step-title">Teléfono o WhatsApp</div>
            {step === 5 && (
              <div className="reg-step-content">
                <input
                  type="tel"
                  value={telefone}
                  onChange={e => {
                    setTelefone(maskTelefono(e.target.value));
                    if (errors.telefone) setErrors(ev => ({ ...ev, telefone: undefined }));
                  }}
                  onKeyDown={e => e.key === 'Enter' && handleNextTelefone()}
                  placeholder="+57 300 000 0000"
                  className={`rsvp-input reg-email-input${errors.telefone ? ' reg-input--error' : ''}`}
                  autoFocus
                />
                {errors.telefone && <p className="reg-field-error">{errors.telefone}</p>}
                <div className="reg-step-footer">
                  <button type="button" className="reg-btn-back" onClick={() => setStep(4)}>Volver</button>
                  <button
                    type="button"
                    style={{ cursor: 'pointer' }}
                    className="rsvp-btn-pill"
                    onClick={handleNextTelefone}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
            {step > 5 && <div className="reg-step-summary">{telefone}</div>}
          </div>
        </div>

        {/* Step 6: Accesibilidad */}
        <div className={stepClass(6)}>
          <div className="reg-step-number">6</div>
          <div className="reg-step-body">
            <div className="reg-step-title">
              ¿Requiere alguna adaptación de accesibilidad o apoyo especial para participar del evento?
            </div>
            {step === 6 && (
              <div className="reg-step-content">
                <p className="rsvp-form-help">
                  Por ejemplo: apoyo para movilidad, acceso para silla de ruedas, asistencia para
                  desplazamiento u otra necesidad.
                </p>
                <div className="reg-choice-group">
                  <label className={`reg-choice${acessibilidade === 'no' ? ' reg-choice--active' : ''}`}>
                    <input
                      type="radio"
                      name="acessibilidade"
                      checked={acessibilidade === 'no'}
                      onChange={() => {
                        setAcessibilidade('no');
                        setErrors(ev => ({ ...ev, acessibilidade: undefined }));
                      }}
                    />
                    <span>No</span>
                  </label>
                  <label className={`reg-choice${acessibilidade === 'si' ? ' reg-choice--active' : ''}`}>
                    <input
                      type="radio"
                      name="acessibilidade"
                      checked={acessibilidade === 'si'}
                      onChange={() => setAcessibilidade('si')}
                    />
                    <span>Sí. Por favor, especifique:</span>
                  </label>
                </div>
                {acessibilidade === 'si' && (
                  <textarea
                    value={acessibilidadeDetalhe}
                    onChange={e => {
                      setAcessibilidadeDetalhe(e.target.value);
                      if (errors.acessibilidade) setErrors(ev => ({ ...ev, acessibilidade: undefined }));
                    }}
                    placeholder="Describa la adaptación necesaria"
                    className={`rsvp-input reg-email-input reg-textarea${errors.acessibilidade ? ' reg-input--error' : ''}`}
                    maxLength={300}
                    rows={3}
                  />
                )}
                {errors.acessibilidade && <p className="reg-field-error">{errors.acessibilidade}</p>}
                <div className="reg-step-footer">
                  <button type="button" className="reg-btn-back" onClick={() => setStep(5)}>Volver</button>
                  <button
                    type="button"
                    style={{ cursor: 'pointer' }}
                    className="rsvp-btn-pill"
                    onClick={handleNextAcessibilidade}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
            {step > 6 && (
              <div className="reg-step-summary">
                {acessibilidade === 'si' ? acessibilidadeDetalhe : 'No'}
              </div>
            )}
          </div>
        </div>

        {/* Step 7: Restricción alimentaria + Submit */}
        <div className={stepClass(7)}>
          <div className="reg-step-number">7</div>
          <div className="reg-step-body">
            <div className="reg-step-title">
              ¿Tiene alguna alergia, intolerancia o restricción alimentaria que debamos considerar?
            </div>
            {step === 7 && (
              <div className="reg-step-content">
                <div className="reg-choice-group">
                  <label className={`reg-choice${alimentar === 'no' ? ' reg-choice--active' : ''}`}>
                    <input
                      type="radio"
                      name="alimentar"
                      checked={alimentar === 'no'}
                      onChange={() => {
                        setAlimentar('no');
                        setErrors(ev => ({ ...ev, alimentar: undefined }));
                      }}
                    />
                    <span>No</span>
                  </label>
                  <label className={`reg-choice${alimentar === 'si' ? ' reg-choice--active' : ''}`}>
                    <input
                      type="radio"
                      name="alimentar"
                      checked={alimentar === 'si'}
                      onChange={() => setAlimentar('si')}
                    />
                    <span>Sí. Por favor, especifique:</span>
                  </label>
                </div>
                {alimentar === 'si' && (
                  <textarea
                    value={alimentarDetalhe}
                    onChange={e => {
                      setAlimentarDetalhe(e.target.value);
                      if (errors.alimentar) setErrors(ev => ({ ...ev, alimentar: undefined }));
                    }}
                    placeholder="Describa la alergia, intolerancia o restricción"
                    className={`rsvp-input reg-email-input reg-textarea${errors.alimentar ? ' reg-input--error' : ''}`}
                    maxLength={300}
                    rows={3}
                  />
                )}
                {errors.alimentar && <p className="reg-field-error">{errors.alimentar}</p>}
                <div className="reg-step-footer">
                  <button type="button" className="reg-btn-back" onClick={() => setStep(6)}>Volver</button>
                  <button
                    type="button"
                    style={{ cursor: 'pointer' }}
                    className="rsvp-btn-pill"
                    onClick={handleSubmit}
                  >
                    Solicitar mi inscripción
                  </button>
                </div>
                <p className="rsvp-form-help">
                  Los cupos son limitados y la inscripción está sujeta a confirmación.
                </p>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
